import { createStore } from 'vuex'
import axios from 'axios'
import createDataModule from './modules/dataModule'
import notification from './modules/notification'

// Ensure the API URL always ends with /api/v1
const getAPIURL = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  // Remove trailing slash if present
  const cleanUrl = envUrl.replace(/\/$/, '')
  // Add /api/v1 if not already present
  return cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`
}

// Base URL for login (without /api/v1)
const getLoginURL = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  // Remove trailing slash if present
  const cleanUrl = envUrl.replace(/\/$/, '')
  // Remove /api/v1 if present for login
  return cleanUrl.endsWith('/api/v1') ? cleanUrl.replace('/api/v1', '') : cleanUrl
}

const API_URL = getAPIURL()
const LOGIN_URL = getLoginURL()

// Add this before the modules definition
const securityCategoriesModule = {
  namespaced: true,
  state: {
    data: [],
    loading: false,
    error: null
  },
  mutations: {
    setData(state, data) {
      state.data = data
    },
    setLoading(state, loading) {
      state.loading = loading
    },
    setError(state, error) {
      state.error = error
    },
    updateSubCategoryStatus(state, { categoryId, subCategoryId, isEnabled }) {
      const category = state.data.find(c => c.id === categoryId)
      if (category) {
        const subCategory = category.sub_categories.find(sc => sc.id === subCategoryId)
        if (subCategory) {
          subCategory.is_enabled = isEnabled
        }
      }
    }
  },
  actions: {
    async fetchData({ commit, rootState }, { tenantId, page, entriesPerPage, search, orderBy, sortDirection }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.get(`${API_URL}/admin/tenants/${tenantId}/security-categories`, {
          headers: { Authorization: `Bearer ${rootState.token}` },
          params: {
            page,
            per_page: entriesPerPage,
            search,
            order_by: orderBy,
            sort_direction: sortDirection
          }
        })
        commit('setData', response.data.data || [])
        return {
          currentPage: response.data.current_page,
          totalPages: response.data.total_pages,
          entriesPerPage: response.data.per_page,
          totalEntries: response.data.total,
          orderBy: response.data.order_by,
          sortDirection: response.data.sort_direction,
          search: response.data.search
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to fetch security categories'
        commit('setError', errorMessage)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async toggleSubCategory({ commit, rootState }, { tenantId, categoryId, subCategoryId, enabled, content }) {
      commit('setLoading', true)
      try {
        const url = `${API_URL}/admin/tenants/${tenantId}/security-categories/${categoryId}/${subCategoryId}`
        const method = enabled ? 'put' : 'delete'
        const data = enabled ? { content: content || '' } : undefined

        await axios({
          method,
          url,
          data,
          headers: { Authorization: `Bearer ${rootState.token}` }
        })

        commit('updateSubCategoryStatus', { categoryId, subCategoryId, isEnabled: enabled })
        commit('setError', null)  // Clear any existing error on success
        return true
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to toggle subcategory status'
        commit('setError', errorMessage)
        throw error
      } finally {
        commit('setLoading', false)
      }
    }
  }
}

export default createStore({
  state: {
    token: localStorage.getItem('token') || null,
    user: null,
    loading: false,
    error: null,
    tenants: [],
    currentTenantId: localStorage.getItem('currentTenantId') || null,
    documents: [],
    updates: [],
    compliances: [],
    allCompliances: [],
    globalSearchQuery: ''
  },
  getters: {
    isLoggedIn: state => !!state.token,
    getUser: state => state.user,
    isLoading: state => state.loading,
    getError: state => state.error,
    currentTenantId: state => state.currentTenantId,
    currentTenantName: state => {
      const currentTenant = state.tenants.find(t => t.tenant_id === state.currentTenantId)
      return currentTenant ? currentTenant.friendly_name : null
    },
    globalSearchQuery: state => state.globalSearchQuery
  },
  mutations: {
    setToken(state, token) {
      state.token = token
    },
    setUser(state, user) {
      state.user = user
    },
    clearAuth(state) {
      state.token = null
      state.user = null
      state.currentTenantId = null
    },
    setLoading(state, isLoading) {
      state.loading = isLoading
    },
    setError(state, error) {
      state.error = error
    },
    setTenants(state, tenants) {
      state.tenants = tenants
    },
    setSelectedTenant(state, tenantId) {
      state.selectedTenant = tenantId
    },
    setCurrentTenantId(state, tenantId) {
      state.currentTenantId = tenantId
      if (tenantId) {
        localStorage.setItem('currentTenantId', tenantId)
      } else {
        localStorage.removeItem('currentTenantId')
      }
    },
    setDocuments(state, documents) {
      console.log('Setting documents in store:', documents)
      state.documents = documents
    },
    setUpdates(state, updates) {
      console.log('Setting updates in store:', updates)
      state.updates = updates
    },
    setCompliances(state, compliances) {
      console.log('Setting compliances in store:', compliances)
      state.compliances = compliances
    },
    setAllCompliances(state, compliances) {
      console.log('Setting all compliances in store:', compliances)
      state.allCompliances = compliances
    },
    SET_GLOBAL_SEARCH(state, query) {
      state.globalSearchQuery = query
    }
  },
  actions: {
    async login({ commit }, credentials) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.post(`${LOGIN_URL}/login`, credentials)
        // Three possible response shapes:
        //   { token } → normal success, commit session
        //   { mfa_required: true } → user has TOTP enabled, need 6-digit code
        //   { mfa_enrollment_required, enrollment_token } → admin forces enrol
        // In the last two cases we return the data to the caller without
        // committing a session; Login.vue drives the UI from there.
        if (response.data.token) {
          localStorage.setItem('token', response.data.token)
          commit('setToken', response.data.token)
          commit('setUser', { email: credentials.email })
        }
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Login failed')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    // setSessionFromToken lets Login.vue install a JWT obtained from /mfa/confirm
    // (after enrolment) without duplicating the commit/localStorage plumbing.
    setSessionFromToken({ commit }, { token, email }) {
      localStorage.setItem('token', token)
      commit('setToken', token)
      commit('setUser', { email: email || '' })
    },
    async loginWithEmail({ commit }, email) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.post(`${LOGIN_URL}/login/email`, { email })
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to send login email')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    async loginWithToken({ commit }, token) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.post(`${LOGIN_URL}/login/token`, { token })
        const jwtToken = response.data.token
        localStorage.setItem('token', jwtToken)
        commit('setToken', jwtToken)
        // Note: Backend doesn't return user data, so we'll set a basic user object
        commit('setUser', { email: 'user@example.com' }) // We don't have email from token login
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Invalid or expired login token')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    logout({ commit }) {
      localStorage.removeItem('token')
      commit('clearAuth')
    },
    async fetchTenants({ commit, state }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.get(`${API_URL}/admin/tenants`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        commit('setTenants', response.data)
        
        // If we have a current tenant ID but it's not in the list, clear it
        if (state.currentTenantId && !response.data.find(t => t.tenant_id === state.currentTenantId)) {
          commit('setCurrentTenantId', null)
        }
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to fetch tenants')
      } finally {
        commit('setLoading', false)
      }
    },
    async fetchUsers({ commit, state }, tenantId) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.get(`${API_URL}/admin/tenants/${tenantId}/users`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to fetch users')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    async fetchSecurityCategories({ commit, state }, tenantId) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.get(`${API_URL}/admin/tenants/${tenantId}/security-categories`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to fetch security categories')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    async fetchUpdates({ commit, state }, tenantId) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.get(`${API_URL}/admin/tenants/${tenantId}/updates`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to fetch updates')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    async fetchDocuments({ commit, state }, tenantId) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.get(`${API_URL}/admin/tenants/${tenantId}/documents`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        console.log('Documents fetched from API:', response.data)
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to fetch documents')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    async fetchCompliances({ commit, state }, tenantId) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.get(`${API_URL}/admin/tenants/${tenantId}/compliances`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to fetch compliances')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    setCurrentTenant({ commit }, tenantId) {
      commit('setCurrentTenantId', tenantId)
    },
    async fetchAllCompliances({ commit, state }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.get(`${API_URL}/admin/compliances`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        return response.data
      } catch (error) {
        console.error('Error fetching all compliances:', error)
        commit('setError', error.response?.data?.message || 'Failed to fetch all compliances')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    setGlobalSearch({ commit }, query) {
      commit('SET_GLOBAL_SEARCH', query)
    },
    async createDocument({ commit, state }, { tenantId, documentData }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.post(
          `${API_URL}/admin/tenants/${tenantId}/documents`,
          documentData,
          {
            headers: {
              Authorization: `Bearer ${state.token}`,
              'Content-Type': 'application/json'
            }
          }
        )
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to create document')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    async uploadDocumentFile({ commit, state }, { tenantId, documentId, file }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const formData = new FormData()
        formData.append('file', file)
        
        const response = await axios.post(
          `${API_URL}/admin/tenants/${tenantId}/documents/${documentId}`,
          formData,
          {
            headers: { Authorization: `Bearer ${state.token}` }
          }
        )
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to upload document file')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    async createDocumentWithFile({ dispatch, commit }, { tenantId, documentData, file }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        // First create the document metadata
        const response = await dispatch('createDocument', { tenantId, documentData })

        // If we have a file, wait a moment and then upload it
        if (file) {
          // Add a small delay to ensure document is fully created
          await new Promise(resolve => setTimeout(resolve, 1000))

          // Upload the file using the new document ID
          await dispatch('uploadDocumentFile', {
            tenantId,
            documentId: response.id,
            file
          })
        }

        return response
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to create document with file')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    async getDocumentFileURL({ commit, state }, { tenantId, documentId }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await axios.get(
          `${API_URL}/admin/tenants/${tenantId}/documents/${documentId}`,
          {
            headers: { Authorization: `Bearer ${state.token}` }
          }
        )
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to get document file URL')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    async getTenantData({ state }, tenantId) {
      const response = await axios.get(`${API_URL}/admin/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${state.token}` }
      })
      return response.data
    },
    async updateTenantData({ state }, { tenantId, data }) {
      const response = await axios.put(`${API_URL}/admin/tenants/${tenantId}`, data, {
        headers: { Authorization: `Bearer ${state.token}` }
      })
      return response.data
    },
    async fetchAuditLogs({ state }, { tenantId, params }) {
      const response = await axios.get(`${API_URL}/admin/tenants/${tenantId}/audit-logs`, {
        headers: { Authorization: `Bearer ${state.token}` },
        params: {
          page: params.page,
          per_page: params.per_page,
          order_by: params.order_by,
          sort_direction: params.sort_direction,
          search: params.search,
          start_date: params.start_date,
          end_date: params.end_date,
          action: params.action,
          resource: params.resource,
          user_id: params.user_id,
          status: params.status
        }
      })
      return response.data
    },
    async deleteUser({ state }, { tenantId, userId }) {
      await axios.delete(`${API_URL}/admin/tenants/${tenantId}/users/${userId}`, {
        headers: { Authorization: `Bearer ${state.token}` }
      })
      return true
    },

    async startOnboarding({ state }, { tenantId, companyUrl, forceRerun = false }) {
      try {
        const url = `${API_URL}/admin/tenants/${tenantId}/onboarding` +
          (forceRerun ? '?force_rerun=true' : '')
        const response = await axios.post(url, {
          company_url: companyUrl
        }, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        return response.data
      } catch (error) {
        // Provide more specific error messages based on the error type
        let errorMessage = error.response?.data?.error || error.response?.data?.message || 'Onboarding failed'
        
        // Check for specific error patterns in the message
        if (errorMessage.includes('403') || errorMessage.includes('access denied') || errorMessage.includes('forbidden')) {
          errorMessage = 'The company website is not accessible. This could be due to access restrictions, authentication requirements, or the website blocking automated access. Please try with a different website or contact support.'
        } else if (errorMessage.includes('timeout')) {
          errorMessage = 'The website analysis timed out. This could be due to a slow website or network issues. Please try again or use a different website.'
        } else if (errorMessage.includes('connection') || errorMessage.includes('network')) {
          errorMessage = 'Unable to connect to the website. Please check the URL and ensure the website is accessible.'
        } else if (errorMessage.includes('invalid URL') || errorMessage.includes('malformed')) {
          errorMessage = 'The provided URL is invalid. Please check the format and try again.'
        } else if (errorMessage.includes('Failed to fetch website')) {
          errorMessage = 'Unable to access the company website. This could be due to access restrictions, authentication requirements, or the website blocking automated access. Please try with a different website or contact support.'
        }

        // Preserve the HTTP status and backend error code on the rethrown
        // error. Callers distinguish "already onboarded" (403 /
        // ALREADY_ONBOARDED) and "already running" (409) from genuine
        // failures, and a bare Error(message) throws that information away.
        const wrapped = new Error(errorMessage)
        wrapped.status = error.response?.status
        wrapped.code = error.response?.data?.code
        throw wrapped
      }
    },

    // Whether this tenant has ever completed AI onboarding. Used to decide
    // if the one-time prompt should be shown, without POSTing to find out.
    async fetchTenantOnboarding({ state }, tenantId) {
      const response = await axios.get(`${API_URL}/admin/tenants/${tenantId}/onboarding`, {
        headers: { Authorization: `Bearer ${state.token}` }
      })
      return response.data
    },

    async getOnboardingStatus({ state }, { tenantId, statusId }) {
      try {
        const response = await axios.get(`${API_URL}/admin/tenants/${tenantId}/onboarding/status/${statusId}`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        return response.data
      } catch (error) {
        // Provide more specific error messages based on the error type
        let errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to get status'
        
        // Check for specific error patterns in the message
        if (errorMessage.includes('403') || errorMessage.includes('access denied') || errorMessage.includes('forbidden')) {
          errorMessage = 'The company website is not accessible. This could be due to access restrictions, authentication requirements, or the website blocking automated access. Please try with a different website or contact support.'
        } else if (errorMessage.includes('timeout')) {
          errorMessage = 'The website analysis timed out. This could be due to a slow website or network issues. Please try again or use a different website.'
        } else if (errorMessage.includes('connection') || errorMessage.includes('network')) {
          errorMessage = 'Unable to connect to the website. Please check the URL and ensure the website is accessible.'
        } else if (errorMessage.includes('invalid URL') || errorMessage.includes('malformed')) {
          errorMessage = 'The provided URL is invalid. Please check the format and try again.'
        } else if (errorMessage.includes('Failed to fetch website')) {
          errorMessage = 'Unable to access the company website. This could be due to access restrictions, authentication requirements, or the website blocking automated access. Please try with a different website or contact support.'
        }
        
        throw new Error(errorMessage)
      }
    }
  },
  modules: {
    securityCategories: securityCategoriesModule,
    users: createDataModule('users', `${API_URL}/admin/tenants/:tenantId/users`),
    updates: createDataModule('updates', `${API_URL}/admin/tenants/:tenantId/updates`),
    compliances: createDataModule('compliances', `${API_URL}/admin/tenants/:tenantId/compliances`),
    documents: createDataModule('documents', `${API_URL}/admin/tenants/:tenantId/documents`),
    vulnerabilities: createDataModule('vulnerabilities', `${API_URL}/admin/tenants/:tenantId/vulns`),
    products: createDataModule('products', `${API_URL}/admin/tenants/:tenantId/products`),
    incidents: createDataModule('incidents', `${API_URL}/admin/tenants/:tenantId/incidents`),
    notification
  }
})